import { IDictionary } from './common'

// ============================================================================
// Handler Types
// ============================================================================

/**
 * Generic handler function type
 */
export type Handler = (...arguments_: any[]) => void

/**
 * Dictionary of handler functions
 */
export type Handlers = IDictionary<Handler>

/**
 * Push notification handler function type
 */
export type PushHandler = (...arguments_: any[]) => void

/**
 * Dictionary of push handlers
 */
export type PushHandlers = IDictionary<PushHandler>

/**
 * CXF event handler/subscribers
 */
export type CxfEventHandlers = Handler[]

// ============================================================================
// Event Payload Types
// ============================================================================

/**
 * Payload for opening IGS (In-Game Shop)
 */
export interface OpenIGSPayload {
    page?: string
    route?: string
    sourceId?: string
    config?: Record<string, any>
}

/**
 * Payload for reward events
 */
export interface OnRewardProperties {
    successUrl: string
    offerId: string
    sid: string
    page: string
    config?: Record<string, any>
}

/**
 * Payload for Lemonstand category update events
 */
export interface OnLemonstandCategoryUpdateProperties {
    target: string
    action: string
    data: any
}

/**
 * Payload for Lemonstand notification creation events
 */
export interface OnLemonstandNotificationsCreatedProperties {
    notifCount: number
}

// ============================================================================
// Tracking Types
// ============================================================================

/**
 * Generic tracking payload
 */
export interface ITrackPayload extends IDictionary<string | number> {
    eventId: number
}

/**
 * Properties for Ranch webshop call tracking
 */
export interface IRanchWebShopCallProperties extends ITrackPayload {
    gameId: number
    playerId: number
    instanceId: number
    zoneId: number
    networkId: number
    sessionId: string
    date: number
    unixtimeMS: number
    sourceId: string
}

// ============================================================================
// URL Types
// ============================================================================

/**
 * Properties for creating catalog/iframe URLs
 */
export interface ICreateCatalogUrlProperties {
    sid?: string
    page?: string
    route?: string
    config?: Record<string, any>
}

// ============================================================================
// Webshop Events Enum
// ============================================================================

/**
 * Webshop event names
 */
export enum WebshopEvents {
    CXF_DIALOG_CLOSE = 'cxf.dialog.close',
    CXF_NATIVE_SUBSCRIPTION_ENABLE = 'cxf.native.subscription.enable',
    CXF_OPEN_SALES_MSG = 'cxf.webshop.sales.open',
    CXF_SET_CUSTOMIZATION_SUFFIX = 'cxf.set.customization.surfix',
    CXF_JOIN_TEMP_SERVER = 'cxf.join.temp.server',
    LEMONSTAND_CATEGORY_UPDATE = 'lemonstand.category.update',
    LEMONSTAND_NOTIFICATIONS_CREATED = 'lemonstand.notifs.created',
}

// CANVAS Agent
//
// Agent Props workaround; don't want to make Agent as a package
export enum CanvasAppEvent {
    CHECKOUT = 'checkout',
    CLOSE = 'close',
    COLLECT = 'collect',
    ERROR = 'error',
    EXPIRED = 'expired',
    INIT = 'init',
    NATIVE_CATALOG_REQUEST = 'native_catalog_request',
    NATIVE_CHECKOUT_REQUEST = 'native_checkout_request',
    NAVIGATE = 'navigate',
    READY = 'ready',
    RESIZE = 'resize',
}

export type CanvasEventPostMessage = {
    eventType: CanvasAppEvent
    [key: string]: any
}

export type AgentProps = {
    canvasAppId: string
    eventListener?: (event: CanvasEventPostMessage) => void
    frameParent?: HTMLElement
    fullScreen?: boolean
    locale?: string
    sessionId?: string
    token: string
    customContext?: Record<string, any>
    authClientId?: any
    debug?: boolean
}

export * from './config'
export * from './models'
export * from './store'
